import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Panel, InlineWarning, ListingLinkButton } from "@/components/common/Primitives";
import { api } from "@/lib/api";
import { dateOnly, dateTime, eur, eurSigned, km, num, pct } from "@/lib/format";
import { SCENARIOS } from "@/components/vehicle/constants";

const CHART_TICK = { fontSize: 10 };
const CHART_DOT = { r: 2 };

export const WaterfallPanel = ({ costs, chosen, scenario, onOpenCost }) => (
  <Panel
    title="Financiële waterfall"
    subtitle="Landed cost opbouw"
    testid="waterfall-panel"
    actions={
      <button
        type="button"
        onClick={onOpenCost}
        data-testid="open-cost-dialog"
        className="text-[11px] font-semibold text-blue-700 hover:underline"
      >
        Kostenopbouw bekijken
      </button>
    }
  >
    <div>
      {(costs?.lines || []).map((line) => (
        <div
          key={line.key}
          className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5"
        >
          <span className="flex items-center gap-1.5 text-[12.5px] text-slate-700">
            {line.label}
            {line.assumption && (
              <span className="chip border-slate-300 bg-slate-100 text-[9px] text-slate-500">
                standaard aanname
              </span>
            )}
          </span>
          <span className={`mono text-[12.5px] ${line.unresolved ? "text-red-700" : "text-slate-900"}`}>
            {line.amount === null ? "Niet berekenbaar" : eur(line.amount)}
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-1.5">
        <span className="text-[12.5px] font-semibold text-slate-800">Subtotaal vóór onvoorzien</span>
        <span className="mono text-[12.5px] font-semibold">
          {eur(costs?.landed_cost_before_contingency)}
        </span>
      </div>
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5">
        <span className="text-[12.5px] text-slate-700">{costs?.contingency?.label}</span>
        <span className="mono text-[12.5px]">{eur(costs?.contingency?.amount)}</span>
      </div>
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-100 px-3 py-2">
        <span className="font-display text-[13px] font-bold text-slate-900">TOTALE LANDED COST</span>
        <span className="mono text-[14px] font-bold text-slate-900" data-testid="detail-landed-cost">
          {eur(chosen?.landed_cost)}
        </span>
      </div>
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5">
        <span className="text-[12.5px] text-slate-700">
          Verwachte Nederlandse verkoopprijs ({SCENARIOS.find((s) => s.key === scenario)?.label})
        </span>
        <span className="mono text-[12.5px] font-semibold">{eur(chosen?.sale_value)}</span>
      </div>
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5">
        <span className="text-[12.5px] text-slate-700">
          − Verkoop-BTW NL ({chosen?.sales_vat_pct ?? 21}%)
        </span>
        <span className="mono text-[12.5px] text-red-700" data-testid="detail-sales-vat">
          {chosen?.sales_vat != null ? `− ${eur(chosen.sales_vat)}` : "—"}
        </span>
      </div>
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-1.5">
        <span className="text-[12.5px] font-semibold text-slate-800">Netto-opbrengst NL</span>
        <span className="mono text-[12.5px] font-semibold" data-testid="detail-net-sale-value">
          {eur(chosen?.net_sale_value)}
        </span>
      </div>
      <div className="flex items-center justify-between bg-emerald-50 px-3 py-2.5">
        <span className="font-display text-[13px] font-bold text-emerald-900">
          POTENTIËLE HANDELSBIJDRAGE
        </span>
        <div className="text-right">
          <div
            className={`mono text-[16px] font-bold ${
              (chosen?.contribution ?? 0) > 0 ? "text-emerald-700" : "text-red-700"
            }`}
            data-testid="detail-contribution"
          >
            {eurSigned(chosen?.contribution, { fallback: "Niet berekenbaar" })}
          </div>
          <div className="mono text-[11px] text-emerald-800">
            ROI {pct(chosen?.roi_pct, { fallback: "—" })}
          </div>
        </div>
      </div>
    </div>
    <div className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-500">
      Trading Contribution — vaste bedrijfskosten, financiering en belastingen zijn niet meegenomen.
      Vóór onvoorzien:{" "}
      <span className="mono">
        {eurSigned(chosen?.contribution_before_contingency, { fallback: "—" })}
      </span>{" "}
      (ROI {pct(chosen?.roi_before_contingency_pct, { fallback: "—" })}).
    </div>
  </Panel>
);

export const ComparablesPanel = ({
  analysis,
  head,
  listingId,
  onReload,
  onOpenValuation,
  onOpenMarketValue,
}) => {
  const comparables = analysis?.valuation?.comparables_used || [];
  return (
    <Panel
      title="Nederlandse vergelijkingsauto's"
      subtitle={`${analysis?.valuation?.n_used ?? 0} gebruikt, ${
        analysis?.valuation?.n_excluded ?? 0
      } uitgesloten`}
      testid="comparables-panel"
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenValuation}
            data-testid="open-valuation-dialog"
            className="text-[11px] font-semibold text-blue-700 hover:underline"
          >
            Waardering bekijken
          </button>
          <button
            type="button"
            onClick={onOpenMarketValue}
            data-testid="open-market-value"
            className="rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            Verwacht NL aanpassen
          </button>
        </div>
      }
    >
      {comparables.length === 0 ? (
        <div className="p-3">
          <InlineWarning tone="danger" testid="no-comparables-warning">
            Onvoldoende marktdata voor betrouwbare waardering. Er zijn geen Nederlandse
            vergelijkingsauto's opgeslagen voor dit model.
          </InlineWarning>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Voertuig</th>
                <th>1e toelating</th>
                <th>KM</th>
                <th>Verkoper</th>
                <th>Prijs</th>
                <th>Verschil</th>
                <th>Overeenkomst</th>
                <th>URL</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {comparables.map((c) => (
                <tr key={c.listing_id}>
                  <td>
                    {c.image_url ? (
                      <img
                        src={c.image_url}
                        alt=""
                        className="h-8 w-12 rounded-sm border border-slate-200 object-cover"
                      />
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                  <td className="max-w-[220px] truncate">
                    {[c.make, c.model, c.variant].filter(Boolean).join(" ")}
                  </td>
                  <td className="num">{dateOnly(c.first_registration)}</td>
                  <td className="num">{km(c.mileage_km)}</td>
                  <td className="text-[11.5px]">{c.seller_type || "—"}</td>
                  <td className="num">{eur(c.price)}</td>
                  <td className="num">
                    {eurSigned((c.price ?? 0) - (head.expected_market_value ?? 0))}
                  </td>
                  <td className="num">{num(c.comparison_score ?? c.similarity, { decimals: 1 })}</td>
                  <td>
                    <ListingLinkButton url={c.url} testid={`comparable-url-${c.listing_id}`} />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={async () => {
                        await api.post(`/listings/${listingId}/comparables/${c.listing_id}/exclude`, {
                          reason: "Niet vergelijkbaar volgens gebruiker",
                        });
                        onReload();
                      }}
                      data-testid={`detail-exclude-${c.listing_id}`}
                      className="rounded-sm border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 transition-colors duration-150 hover:border-red-400 hover:text-red-700"
                    >
                      Niet vergelijkbaar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

export const PriceHistoryPanel = ({ priceHistory, listing }) => (
  <Panel title="Prijsgeschiedenis" testid="price-history-panel">
    {priceHistory.length < 2 ? (
      <p className="px-3 py-4 text-[12px] text-slate-500">
        Nog maar één observatie opgeslagen. Prijsverlagingen worden zichtbaar zodra de advertentie
        opnieuw is gescand.
      </p>
    ) : (
      <div className="h-48 p-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={priceHistory}>
            <XAxis dataKey="date" tick={CHART_TICK} stroke="#94a3b8" />
            <YAxis tick={CHART_TICK} stroke="#94a3b8" tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => eur(v)} />
            <Line type="stepAfter" dataKey="price" stroke="#1d4ed8" strokeWidth={2} dot={CHART_DOT} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}
    {listing.price_change < 0 && (
      <div className="border-t border-slate-100 px-3 py-2 text-[12px] font-semibold text-emerald-700">
        Prijs verlaagd: {eur(listing.previous_price)} → {eur(listing.price)} (
        {eurSigned(listing.price_change)})
      </div>
    )}
  </Panel>
);

// Bouwt de datapunten voor de prijsgeschiedenis-grafiek.
export const buildPriceHistory = (rows) =>
  (rows || []).map((s) => ({ date: dateTime(s.observed_at), price: s.price }));
